import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Loader2, ScanFace, LogOut, ShieldCheck } from 'lucide-react';
import {
  isMobileDevice, platformAuthAvailable, passkeyStatus,
  registerPasskey, authenticatePasskey, resetPasskey, isUnlocked, setUnlocked,
} from '../lib/webauthn';

const errText = (e) => {
  if (e?.name === 'NotAllowedError') return 'Abgebrochen oder nicht erlaubt — bitte erneut versuchen.';
  return e?.response?.data?.detail || e?.message || 'Fehlgeschlagen — bitte erneut versuchen.';
};

const GRACE_MS = 60_000; // 1 minute grace period when switching apps (user request, 19.08.2026)

export const PasskeyLock = () => {
  const { logout } = useAuth();
  const mobile = isMobileDevice();
  const [state, setState] = useState(() => (!mobile ? 'off' : isUnlocked() ? 'unlocked' : 'checking'));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const busyRef = useRef(false);
  const stateRef = useRef(state);
  const hiddenAtRef = useRef(null);
  const graceTimerRef = useRef(null);
  useEffect(() => { stateRef.current = state; }, [state]);

  useEffect(() => {
    if (!mobile) return;
    let cancelled = false;
    const init = async () => {
      if (isUnlocked()) { setState('unlocked'); return; }
      if (!(await platformAuthAvailable())) { if (!cancelled) setState('off'); return; }
      try {
        const st = await passkeyStatus();
        if (cancelled) return;
        setState(st.has_passkey ? 'locked' : 'setup');
      } catch {
        if (!cancelled) setState('off');
      }
    };
    init();

    const lock = () => {
      setUnlocked(false);
      setState(s => (s === 'unlocked' ? 'locked' : s));
    };
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        clearTimeout(graceTimerRef.current);
        graceTimerRef.current = setTimeout(lock, GRACE_MS);
      } else {
        clearTimeout(graceTimerRef.current);
        if (hiddenAtRef.current && Date.now() - hiddenAtRef.current > GRACE_MS) lock();
        hiddenAtRef.current = null;
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearTimeout(graceTimerRef.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [mobile]);

  const unlock = async (auto = false) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true); setError('');
    try {
      await authenticatePasskey();
      setUnlocked(true);
      setState('unlocked');
    } catch (e) {
      if (!auto) setError(errText(e));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  // A tap ANYWHERE on the lock screen starts Face ID directly (user gesture =>
  // iOS skips its "use passkey?" confirmation sheet). Zero-tap is impossible for
  // websites on iOS — this is the fastest allowed flow.
  const onOverlayTap = () => {
    if (stateRef.current === 'locked' && !busyRef.current) unlock(false);
  };

  const setup = async () => {
    setBusy(true); setError('');
    try {
      await registerPasskey();
      setUnlocked(true);
      setState('unlocked');
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const reRegister = async () => {
    if (!window.confirm('Face ID neu einrichten? Der bisherige Zugang (z. B. im Passwort-Manager gespeichert) wird entfernt.\n\nWichtig: Wenn danach dein Passwort-Manager (z. B. LastPass) erscheint, tippe auf „Weitere Optionen" und wähle „iPhone" — dann entsperrt die App künftig direkt mit Face ID.')) return;
    setBusy(true); setError('');
    try {
      await resetPasskey();
      setState('setup');
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  if (state === 'off' || state === 'unlocked') return null;

  return (
    <div className="fixed inset-0 z-[200] bg-background flex flex-col items-center justify-center px-8 text-center"
      onPointerDown={onOverlayTap}
      data-testid="passkey-lock-overlay">
      <p className="font-heading text-sm tracking-[0.3em] text-foreground/50 uppercase mb-10">OLTA Nachfolge</p>

      {state === 'checking' && (
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" data-testid="passkey-checking" />
      )}

      {state === 'setup' && (
        <>
          <ShieldCheck className="w-12 h-12 text-sky-400 mb-4" />
          <h1 className="font-heading text-xl text-foreground mb-2">Face ID einrichten</h1>
          <p className="text-sm text-muted-foreground max-w-xs mb-4">
            Zum Schutz deiner Deal-Daten ist Face ID / Touch ID auf Mobilgeräten erforderlich.
            Beim kurzen App-Wechsel (unter 1 Minute) bleibt die App entsperrt.
          </p>
          <p className="text-xs text-sky-300/90 max-w-xs mb-8" data-testid="passkey-setup-hint">
            Tipp: Falls dein Passwort-Manager (z. B. LastPass) erscheint, tippe auf
            „Weitere Optionen" und wähle „iPhone" — dann entsperrt die App direkt mit Face ID,
            ohne Passkey-Dialog.
          </p>
          <Button onClick={setup} disabled={busy} data-testid="passkey-setup-btn"
            className="bg-sky-600 hover:bg-sky-500 text-white rounded-sm h-11 px-8 text-sm">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanFace className="w-4 h-4 mr-2" />}
            Face ID jetzt einrichten
          </Button>
        </>
      )}

      {state === 'locked' && (
        <>
          <ScanFace className="w-12 h-12 text-sky-400 mb-4" />
          <h1 className="font-heading text-xl text-foreground mb-2">App gesperrt</h1>
          <p className="text-sm text-muted-foreground max-w-xs mb-8">
            Tippe irgendwo — Face ID startet sofort.
          </p>
          <Button disabled={busy} data-testid="passkey-unlock-btn"
            className="bg-sky-600 hover:bg-sky-500 text-white rounded-sm h-11 px-8 text-sm">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanFace className="w-4 h-4 mr-2" />}
            {busy ? 'Face ID…' : 'Zum Entsperren tippen'}
          </Button>
        </>
      )}

      {error && (
        <p className="text-xs text-red-400 mt-4 max-w-xs" data-testid="passkey-error">{error}</p>
      )}

      {state !== 'checking' && (
        <div className="flex flex-col items-center gap-3 mt-10" onPointerDown={(e) => e.stopPropagation()}>
          {state === 'locked' && (
            <button onClick={reRegister} disabled={busy} data-testid="passkey-rereg-btn"
              className="text-xs text-muted-foreground hover:text-sky-300 transition-colors max-w-xs">
              Passkey-Dialog statt Face ID? → Face ID neu einrichten
            </button>
          )}
          <button onClick={logout} data-testid="passkey-logout-btn"
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <LogOut className="w-3.5 h-3.5" /> Abmelden
          </button>
        </div>
      )}
    </div>
  );
};
